import { ClassStatus } from '@fitness-force/enum';
import { getEarlyCancelWindowEnd } from '@fitness-force/models';

export type B2cBookingDisplayStatus = 'not_opened' | 'accepts' | 'closed';

export interface ClassWindowStatusInput {
  start_datetime: string | Date;
  booking_opens_in_minutes?: number | null;
  booking_closes_in_minutes?: number | null;
  booking_waitlist_closes_in_minutes?: number | null;
  booking_late_cancel_in_minutes?: number | null;
  min_booking_cancel_time?: number | null;
  max_waitlist?: number | null;
  status?: string | null;
}

function parseStart(start: string | Date): Date | null {
  const d = start instanceof Date ? start : new Date(start);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toStartIso(start: string | Date): string {
  return start instanceof Date ? start.toISOString() : String(start);
}

function getLateCancelMinutes(input: ClassWindowStatusInput): number | null {
  const minutes =
    input.booking_late_cancel_in_minutes ?? input.min_booking_cancel_time;
  if (minutes == null || minutes < 0) {
    return null;
  }
  return minutes;
}

export function isEarlyCancelWindowOpen(
  input: ClassWindowStatusInput,
  now: Date = new Date(),
): boolean {
  const end = getEarlyCancelWindowEnd(
    toStartIso(input.start_datetime),
    getLateCancelMinutes(input),
  );
  if (!end) {
    return false;
  }
  return now.getTime() < end.getTime();
}

export function isWaitlistRosterOpen(
  input: ClassWindowStatusInput,
  now: Date = new Date(),
): boolean {
  if ((input.max_waitlist ?? 0) <= 0) {
    return false;
  }
  const start = parseStart(input.start_datetime);
  if (!start) {
    return false;
  }
  const minutes = input.booking_waitlist_closes_in_minutes;
  if (minutes == null || minutes < 0) {
    return false;
  }
  const closeAt = new Date(start.getTime() - minutes * 60 * 1000);
  return now.getTime() < closeAt.getTime();
}

export function getB2cBookingDisplayStatus(
  input: ClassWindowStatusInput,
  now: Date = new Date(),
): B2cBookingDisplayStatus {
  const status = (input.status ?? '').toLowerCase();
  if (
    status === ClassStatus.Cancelled.toLowerCase() ||
    status === ClassStatus.Completed.toLowerCase()
  ) {
    return 'closed';
  }

  const start = parseStart(input.start_datetime);
  if (!start) {
    return 'closed';
  }

  const opensMinutes = input.booking_opens_in_minutes;
  const closesMinutes = input.booking_closes_in_minutes;
  if (opensMinutes == null || opensMinutes < 0) {
    return 'closed';
  }
  if (closesMinutes == null || closesMinutes < 0) {
    return 'closed';
  }

  const opensAt = new Date(start.getTime() - opensMinutes * 60 * 1000);
  const closesAt = new Date(start.getTime() - closesMinutes * 60 * 1000);
  const nowMs = now.getTime();

  if (nowMs < opensAt.getTime()) {
    return 'not_opened';
  }
  if (nowMs < closesAt.getTime()) {
    return 'accepts';
  }
  return 'closed';
}

export function getB2cBookingStatusI18nKey(status: B2cBookingDisplayStatus): string {
  switch (status) {
    case 'not_opened':
      return 'SCHEDULE.VIEWCLASS.B2C_BOOKING_NOT_OPENED';
    case 'accepts':
      return 'SCHEDULE.VIEWCLASS.B2C_ACCEPTS_BOOKINGS';
    case 'closed':
      return 'SCHEDULE.VIEWCLASS.B2C_BOOKING_CLOSED_MEMBER_APP';
  }
}

export function getWindowOpenClosedI18nKey(open: boolean): string {
  return open
    ? 'SCHEDULE.VIEWCLASS.WINDOW_OPEN'
    : 'SCHEDULE.VIEWCLASS.WINDOW_CLOSED';
}

export function toClassWindowStatusInput(
  source: ClassWindowStatusInput | null | undefined,
): ClassWindowStatusInput | null {
  if (!source?.start_datetime) {
    return null;
  }
  return source;
}
